/**
 * Compliance Tooltip System
 * AS9100D Audit-Ready Interface Design
 * 
 * This module provides the complete compliance tooltip data structure
 * as specified in HADEN-ATLAS-Inventory-UI-UX-Audit-Ready-v1.md
 * 
 * Three-tier disclosure pattern:
 * - Tier 1: Field label indicator (blue/gray circle)
 * - Tier 2: Quick tooltip on hover (500ms delay)
 * - Tier 3: Full compliance modal on click
 * 
 * @version 1.0.0
 * @date January 2026
 */

/**
 * @typedef {Object} ClauseReference
 * @property {string} number - Clause number e.g., "8.5.2"
 * @property {string} title - Clause title
 * @property {string} summary - Brief summary
 */

/**
 * @typedef {Object} AuditorQA
 * @property {string} question - Typical auditor question
 * @property {string} suggestedAnswer - Suggested answer
 */

/**
 * @typedef {Object} RelatedAction
 * @property {string} label - Button label
 * @property {'procedure' | 'report' | 'example'} action - Action type
 * @property {string} target - Target resource
 */

/**
 * @typedef {Object} QuickInfo
 * @property {ClauseReference} primaryClause - Primary AS9100D clause
 * @property {string} whyExists - 1-2 sentence explanation
 * @property {'active' | 'monitoring' | 'warning'} complianceStatus - Status
 */

/**
 * @typedef {Object} FullContext
 * @property {Object} primaryClause - Full clause details
 * @property {ClauseReference[]} supportingClauses - Related clauses
 * @property {Object} controlDescription - How the control works
 * @property {string[]} evidenceCreated - What audit trail is generated
 * @property {AuditorQA[]} auditorQA - Q&A for auditors
 * @property {RelatedAction[]} relatedActions - Quick actions
 */

/**
 * @typedef {Object} ComplianceTooltip
 * @property {string} id - Unique identifier
 * @property {string} fieldName - Field display name
 * @property {QuickInfo} quickInfo - Tier 2 tooltip data
 * @property {FullContext} fullContext - Tier 3 modal data
 * @property {'critical' | 'high' | 'medium' | 'low'} criticality - Importance level
 */

// AS9100D Clause Definitions
export const AS9100D_CLAUSES = {
  '7.1.3': {
    number: '7.1.3',
    title: 'Infrastructure',
    summary: 'Storage facilities as critical infrastructure',
    fullText: 'The organization shall determine, provide and maintain the infrastructure necessary for the operation of its processes and to achieve conformity of products and services.'
  },
  '7.1.5': {
    number: '7.1.5',
    title: 'Monitoring and Measuring Resources',
    summary: 'Calibrated measuring equipment for inventory counting',
    fullText: 'The organization shall determine and provide the resources needed to ensure valid and reliable results when monitoring or measuring is used to verify the conformity of products and services to requirements.'
  },
  '7.5': {
    number: '7.5',
    title: 'Documented Information',
    summary: 'All records must be controlled, available, and protected',
    fullText: 'The organization\'s quality management system shall include documented information required by this International Standard and documented information determined by the organization as being necessary for the effectiveness of the quality management system.'
  },
  '8.1': {
    number: '8.1',
    title: 'Operational Planning and Control',
    summary: 'Handling, packaging, preservation, and FOD prevention',
    fullText: 'The organization shall plan, implement and control the processes needed to meet the requirements for the provision of products and services.',
    aerospaceNote: 'Considerations for handling, packaging, and preservation; prevention, detection, and removal of foreign objects.'
  },
  '8.1.2': {
    number: '8.1.2',
    title: 'Configuration Management',
    summary: 'Configuration identity must be maintained',
    fullText: 'The organization shall establish, implement, and maintain a process for configuration management appropriate to the product and services that ensures identification and control of physical and functional attributes throughout the product lifecycle.'
  },
  '8.1.4': {
    number: '8.1.4',
    title: 'Prevention of Counterfeit Parts',
    summary: 'Controls to prevent counterfeit parts entering inventory',
    fullText: 'The organization shall plan, implement, and control processes appropriate to the organization and the product for the prevention of counterfeit or suspect counterfeit part use and their inclusion in product(s) delivered to the customer.'
  },
  '8.4.2': {
    number: '8.4.2',
    title: 'Type and Extent of Control',
    summary: 'Receiving inspection and verification activities',
    fullText: 'The organization shall ensure that externally provided processes, products and services do not adversely affect the organization\'s ability to consistently deliver conforming products and services to its customers.',
    aerospaceNote: 'Verification activities shall include inspection or periodic testing when there is high risk of nonconformities including counterfeit parts.'
  },
  '8.5.1': {
    number: '8.5.1',
    title: 'Control of Production and Service Provision',
    summary: 'Accountability for all products during production',
    fullText: 'The organization shall implement production and service provision under controlled conditions.',
    aerospaceNote: 'Accountability for all products during production (e.g., parts quantities, split orders, nonconforming product). Products released for subsequent use pending completion of all required verification activities shall be identified and recorded.'
  },
  '8.5.2': {
    number: '8.5.2',
    title: 'Identification and Traceability',
    summary: 'Unique identification and batch traceability',
    fullText: 'The organization shall use suitable means to identify outputs when it is necessary to ensure the conformity of products and services.',
    aerospaceNote: 'Traceability requirements include the ability to trace all products manufactured from the same batch of raw material, or from the same manufacturing batch, to the destination (delivery, scrap). Accept/reject/hold status shall be maintained.'
  },
  '8.5.3': {
    number: '8.5.3',
    title: 'Property Belonging to Customers or External Providers',
    summary: 'Customer-furnished material management',
    fullText: 'The organization shall exercise care with property belonging to customers or external providers while it is under the organization\'s control or being used by the organization.'
  },
  '8.5.4': {
    number: '8.5.4',
    title: 'Preservation',
    summary: 'Shelf life, FIFO, handling, and storage requirements',
    fullText: 'The organization shall preserve the outputs during production and service provision, to the extent necessary to ensure conformity to requirements.',
    aerospaceNote: 'Preservation shall include: identification, handling, contamination control, packaging, storage, transmission or transportation, and protection. Cleaning, prevention and detection and removal of foreign objects, sensitive product handling, marking and labeling including safety warnings, shelf life control and stock rotation, hazardous materials handling.'
  },
  '8.5.6': {
    number: '8.5.6',
    title: 'Control of Changes',
    summary: 'Controlled changes to inventory parameters',
    fullText: 'The organization shall review and control changes for production or service provision, to the extent necessary to ensure continuing conformity with requirements.'
  },
  '8.6': {
    number: '8.6',
    title: 'Release of Products and Services',
    summary: 'QC release authorization required before use',
    fullText: 'The organization shall implement planned arrangements, at appropriate stages, to verify that the product and service requirements have been met.',
    aerospaceNote: 'Release shall not proceed until all planned arrangements have been satisfactorily completed. Documented information shall include evidence of conformity with the acceptance criteria and traceability to the person(s) authorizing the release.'
  },
  '8.7': {
    number: '8.7',
    title: 'Control of Nonconforming Outputs',
    summary: 'Quarantine and disposition of nonconforming material',
    fullText: 'The organization shall ensure that outputs that do not conform to their requirements are identified and controlled to prevent their unintended use or delivery.',
    aerospaceNote: 'Product dispositioned for scrap shall be conspicuously and permanently marked, or positively controlled, until physically rendered unusable. Counterfeit, or suspect counterfeit, parts shall be controlled to prevent reentry into the supply chain.'
  },
  '9.1': {
    number: '9.1',
    title: 'Monitoring, Measurement, Analysis and Evaluation',
    summary: 'Inventory accuracy metrics and cycle counting',
    fullText: 'The organization shall determine what needs to be monitored and measured, the methods for monitoring, measurement, analysis and evaluation needed to ensure valid results.'
  },
  '10.2': {
    number: '10.2',
    title: 'Nonconformity and Corrective Action',
    summary: 'Root cause analysis and corrective action for discrepancies',
    fullText: 'When a nonconformity occurs, including any arising from complaints, the organization shall react to the nonconformity and, as applicable take action to control and correct it.'
  }
};

// Criticality levels - Critical clauses get blue indicator, others get gray
export const CRITICAL_CLAUSES = ['8.1.2', '8.1.4', '8.5.1', '8.5.2', '8.5.4', '8.7'];

/**
 * Complete Compliance Tooltip Database
 * Organized by screen/section
 */
export const complianceTooltips = {
  // ===========================================================================
  // LOCATION MANAGEMENT
  // ===========================================================================
  'location.warehouse_code': {
    id: 'location.warehouse_code',
    fieldName: 'Warehouse Code',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['7.1.3'],
      whyExists: 'Identifies storage facility for infrastructure management and audit trails.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['7.1.3'],
        quotedText: 'The organization shall determine, provide and maintain the infrastructure necessary for the operation of its processes.',
      },
      supportingClauses: [],
      controlDescription: {
        howItWorks: [
          'Unique warehouse codes assigned to each facility',
          'Codes are immutable once inventory exists',
          'All inventory transactions reference warehouse'
        ],
        validationRules: [
          'Code must be unique across organization',
          '3-4 character alphanumeric code required'
        ],
        linkages: ['Zones', 'Locations', 'All Inventory Transactions']
      },
      evidenceCreated: [
        'Warehouse master record with creation timestamp',
        'Audit trail of all warehouse configuration changes'
      ],
      auditorQA: [
        {
          question: 'How do you identify storage facilities?',
          suggestedAnswer: 'Each warehouse has a unique code that appears on all inventory records. This enables full traceability of where material is stored.'
        }
      ],
      relatedActions: []
    },
    criticality: 'high'
  },

  'location.zone_type': {
    id: 'location.zone_type',
    fieldName: 'Zone Type',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.5.4'],
      whyExists: 'Defines storage conditions and access controls per material requirements.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.5.4'],
        quotedText: 'Preservation shall include: identification, handling, contamination control, packaging, storage, transmission or transportation, and protection.',
        aerospaceNote: 'Sensitive product handling, hazardous materials handling.'
      },
      supportingClauses: [
        AS9100D_CLAUSES['8.7'],
        AS9100D_CLAUSES['7.1.3']
      ],
      controlDescription: {
        howItWorks: [
          'Zone types enforce storage requirements',
          'QUARANTINE and QC_HOLD zones restrict material access',
          'HAZMAT and ESD zones enforce special handling',
          'System validates part-zone compatibility'
        ],
        validationRules: [
          'Parts can only be stored in compatible zones',
          'Quarantine zones require NCR linkage',
          'ESD-sensitive parts must be in ESD zones'
        ],
        linkages: ['Locations', 'Part Storage Rules', 'Environmental Requirements']
      },
      evidenceCreated: [
        'Zone configuration with environmental specs',
        'Part-zone compatibility matrix',
        'Violations logged if incompatible storage attempted'
      ],
      auditorQA: [
        {
          question: 'How do you ensure ESD-sensitive parts are properly stored?',
          suggestedAnswer: 'ESD-sensitive parts are flagged in the part master. The system only allows storage in ESD-designated zones. Let me show you an example...'
        },
        {
          question: 'How is nonconforming material segregated?',
          suggestedAnswer: 'Nonconforming material is automatically moved to quarantine zones. The system prevents it from being issued until disposition is approved.'
        }
      ],
      relatedActions: []
    },
    criticality: 'critical'
  },

  'location.bin_code': {
    id: 'location.bin_code',
    fieldName: 'Bin Location Code',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.5.2'],
      whyExists: 'Unique bin identification enables precise location tracking for traceability.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.5.2'],
        quotedText: 'The organization shall use suitable means to identify outputs when it is necessary to ensure the conformity of products and services.',
      },
      supportingClauses: [AS9100D_CLAUSES['7.1.3']],
      controlDescription: {
        howItWorks: [
          'Hierarchical code: Zone-Aisle-Rack-Level-Bin',
          'BLN (Bin Location Number) provides scannable 3-char code',
          'Every bin has unique barcode for scanning'
        ],
        validationRules: [
          'Code must be unique within warehouse',
          'Format follows organizational standard',
          'BLN auto-generated using Base-34 encoding'
        ],
        linkages: ['Inventory Balances', 'Pick Lists', 'Put-away Instructions']
      },
      evidenceCreated: [
        'Location master with full hierarchy',
        'BLN barcode assignment record',
        'Movement history by location'
      ],
      auditorQA: [
        {
          question: 'How do you locate material in the warehouse?',
          suggestedAnswer: 'Every bin has a unique location code and scannable BLN. The system shows exact quantities at each location with full movement history.'
        }
      ],
      relatedActions: []
    },
    criticality: 'high'
  },

  // ===========================================================================
  // STOCK INQUIRY
  // ===========================================================================
  'stock.part_number': {
    id: 'stock.part_number',
    fieldName: 'Part Number',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.5.2'],
      whyExists: 'Unique part identification enables traceability throughout product lifecycle.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.5.2'],
        quotedText: 'The organization shall control the unique identification of the outputs when traceability is a requirement.',
      },
      supportingClauses: [AS9100D_CLAUSES['8.1.2']],
      controlDescription: {
        howItWorks: [
          'HPN (HADEN Part Number) provides unique identification',
          'Includes classification code and check digit',
          'Links to all inventory, orders, and usage'
        ],
        validationRules: [
          'Must exist in Part Master before inventory can be created',
          'Check digit validation ensures data integrity'
        ],
        linkages: ['Part Master', 'All Inventory Records', 'BOM Usage']
      },
      evidenceCreated: [
        'Part master record',
        'Complete inventory history by part',
        'Forward trace to all usage'
      ],
      auditorQA: [
        {
          question: 'How do you uniquely identify parts?',
          suggestedAnswer: 'Every part has a unique HPN with built-in classification and check digit. This links to all inventory, orders, and where the part is used.'
        }
      ],
      relatedActions: [
        { label: 'View Part Master', action: 'procedure', target: 'part-detail' }
      ]
    },
    criticality: 'critical'
  },

  'stock.revision': {
    id: 'stock.revision',
    fieldName: 'Part Revision',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.1.2'],
      whyExists: 'Configuration identity must be maintained at inventory level.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.1.2'],
        quotedText: 'The organization shall establish, implement, and maintain a process for configuration management that ensures identification and control of physical and functional attributes.',
      },
      supportingClauses: [AS9100D_CLAUSES['8.5.2']],
      controlDescription: {
        howItWorks: [
          'Revision tracked at lot level from receipt',
          'System warns if issuing superseded revision',
          'Engineering authorization required for non-current revisions'
        ],
        validationRules: [
          'Revision must match released engineering',
          'Cannot mix revisions in same lot'
        ],
        linkages: ['Part Revisions', 'Engineering Change Orders']
      },
      evidenceCreated: [
        'Revision recorded at receipt',
        'Revision mismatch warnings logged',
        'Engineering authorization records'
      ],
      auditorQA: [
        {
          question: 'How do you ensure issued parts match the released design?',
          suggestedAnswer: 'Every lot has the revision recorded at receipt. The system warns if you try to issue a superseded revision and requires engineering authorization.'
        }
      ],
      relatedActions: []
    },
    criticality: 'critical'
  },

  'stock.lot_number': {
    id: 'stock.lot_number',
    fieldName: 'Lot Number',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.5.2'],
      whyExists: 'Unique lot identification enables batch traceability for recalls and quality investigations.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.5.2'],
        quotedText: 'The organization shall control the unique identification of the outputs when traceability is a requirement, and shall retain the documented information necessary to enable traceability.',
        aerospaceNote: 'Traceability requirements include the ability to trace all products manufactured from the same batch of raw material, or from the same manufacturing batch, to the destination.'
      },
      supportingClauses: [
        AS9100D_CLAUSES['8.1.4'],
        AS9100D_CLAUSES['8.7']
      ],
      controlDescription: {
        howItWorks: [
          'Lot numbers are system-generated or validated on entry',
          'Format: LOT-YYYY-NNNNN',
          'Immutable once assigned to received material',
          'Links to: GRN, PO, Supplier, CoC documents'
        ],
        validationRules: [
          'Must be unique within system',
          'Cannot be modified after receipt completion',
          'Required for all lot-tracked parts'
        ],
        linkages: ['GRN', 'PO', 'Supplier', 'Certificate of Conformance', 'Usage Transactions']
      },
      evidenceCreated: [
        'Lot master record with creation timestamp and user',
        'Linkage to source documents (GRN, PO, CoC)',
        'Transaction log of all movements',
        'Forward/backward trace reports available'
      ],
      auditorQA: [
        {
          question: 'How do you ensure lot traceability?',
          suggestedAnswer: 'Every received item is assigned a unique lot number that links to the PO, supplier, and CoC. We can trace forward to see where material was used, or backward to the source. Let me show you a trace report...'
        },
        {
          question: 'Can lot numbers be changed after receipt?',
          suggestedAnswer: 'No, lot numbers are immutable once assigned. The system prevents modification. Here is the audit log showing the original assignment...'
        }
      ],
      relatedActions: [
        { label: 'Run Trace Report', action: 'report', target: 'lot-trace' },
        { label: 'View Lot History', action: 'example', target: 'lot-history' }
      ]
    },
    criticality: 'critical'
  },

  'stock.on_hand': {
    id: 'stock.on_hand',
    fieldName: 'On Hand Quantity',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.5.1'],
      whyExists: 'Real-time accountability for all products during production.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.5.1'],
        quotedText: 'The organization shall implement production and service provision under controlled conditions.',
        aerospaceNote: 'Accountability for all products during production (e.g., parts quantities, split orders, nonconforming product).'
      },
      supportingClauses: [AS9100D_CLAUSES['9.1']],
      controlDescription: {
        howItWorks: [
          'Real-time balance updated on every transaction',
          'Includes: Available + QC Hold + Allocated + Quarantine',
          'Every unit is tracked and accounted for'
        ],
        validationRules: [
          'Balance cannot go negative',
          'All changes require documented transaction'
        ],
        linkages: ['All Inventory Transactions', 'Cycle Counts']
      },
      evidenceCreated: [
        'Real-time balance record',
        'Complete transaction history',
        'Cycle count verification records'
      ],
      auditorQA: [
        {
          question: 'Can you account for all quantities of this part?',
          suggestedAnswer: 'Yes, here\'s the real-time view: X in stock, Y allocated, Z in QC hold, W in quarantine. Every unit is tracked. Here\'s the transaction history showing all movements...'
        }
      ],
      relatedActions: [
        { label: 'View Transaction History', action: 'report', target: 'txn-history' }
      ]
    },
    criticality: 'critical'
  },

  'stock.status': {
    id: 'stock.status',
    fieldName: 'Stock Status',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.5.1'],
      whyExists: 'Inspection status must be visible at all times.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.5.1'],
        quotedText: 'Products released for subsequent use pending completion of all required verification activities shall be identified and recorded.',
      },
      supportingClauses: [
        AS9100D_CLAUSES['8.6'],
        AS9100D_CLAUSES['8.7']
      ],
      controlDescription: {
        howItWorks: [
          'Status tracked: RELEASED, QC_HOLD, QUARANTINE, EXPIRED',
          'Only RELEASED material can be issued to production',
          'Status changes require authorization and are logged'
        ],
        validationRules: [
          'Cannot issue QC_HOLD or QUARANTINE material',
          'Status transitions follow defined workflow'
        ],
        linkages: ['QC Release', 'Quarantine', 'Inspection Records']
      },
      evidenceCreated: [
        'Status change audit trail',
        'QC release authorization records',
        'Quarantine disposition records'
      ],
      auditorQA: [
        {
          question: 'How do you prevent uninspected material from being used?',
          suggestedAnswer: 'Material starts in QC_HOLD status. The system prevents issuing until QC releases it. All status changes are logged with the authorizing user.'
        }
      ],
      relatedActions: []
    },
    criticality: 'critical'
  },

  'stock.qc_hold': {
    id: 'stock.qc_hold',
    fieldName: 'QC Hold',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.4.2'],
      whyExists: 'Verification activities required before release to stock.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.4.2'],
        quotedText: 'The organization shall ensure that externally provided processes, products and services do not adversely affect the organization\'s ability to consistently deliver conforming products and services.',
        aerospaceNote: 'Verification activities shall include inspection or periodic testing when there is high risk of nonconformities including counterfeit parts.'
      },
      supportingClauses: [AS9100D_CLAUSES['8.6']],
      controlDescription: {
        howItWorks: [
          'Material on QC hold pending inspection',
          'Cannot be issued until inspector releases',
          'Tracks days in hold for expediting'
        ],
        validationRules: [
          'QC release requires inspector authorization',
          'E-signature required for release'
        ],
        linkages: ['Inspection Requests', 'QC Release Records']
      },
      evidenceCreated: [
        'QC hold record with entry date',
        'Inspection request and results',
        'Release authorization with e-signature'
      ],
      auditorQA: [
        {
          question: 'How do you verify received material before use?',
          suggestedAnswer: 'All received material goes to QC hold. Inspection is performed per the part\'s inspection plan. Release requires QC inspector authorization with e-signature.'
        }
      ],
      relatedActions: []
    },
    criticality: 'high'
  },

  'stock.expiry': {
    id: 'stock.expiry',
    fieldName: 'Expiry Date',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.5.4'],
      whyExists: 'Shelf life control prevents use of expired material.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.5.4'],
        quotedText: 'The organization shall preserve the outputs during production and service provision.',
        aerospaceNote: 'Shelf life control and stock rotation.'
      },
      supportingClauses: [],
      controlDescription: {
        howItWorks: [
          'Expiry date tracked at lot level',
          'System alerts for approaching expiry (30/60/90 days)',
          'Expired lots automatically blocked from issue',
          'FIFO enforces oldest material used first'
        ],
        validationRules: [
          'Cannot receive already-expired material',
          'Cannot issue expired material',
          'Expiry required for shelf-life controlled parts'
        ],
        linkages: ['Lot Records', 'FIFO Suggestions', 'Expiry Alerts']
      },
      evidenceCreated: [
        'Expiry date recorded at receipt',
        'Expiry alerts generated',
        'Blocked issue attempts logged'
      ],
      auditorQA: [
        {
          question: 'How do you ensure shelf-life controlled items don\'t expire in stock?',
          suggestedAnswer: 'The system tracks expiry dates on every lot. Dashboard shows items expiring within 30/60/90 days. FIFO is enforced—if you pick a newer lot, you must document why. Here\'s an example with the alerts...'
        }
      ],
      relatedActions: [
        { label: 'View Expiring Inventory', action: 'report', target: 'expiry-report' }
      ]
    },
    criticality: 'critical'
  },

  'stock.quarantine_zone': {
    id: 'stock.quarantine_zone',
    fieldName: 'Quarantine Zone',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.7'],
      whyExists: 'Nonconforming material must be segregated to prevent unintended use.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.7'],
        quotedText: 'The organization shall ensure that outputs that do not conform to their requirements are identified and controlled to prevent their unintended use or delivery.',
        aerospaceNote: 'Product dispositioned for scrap shall be conspicuously and permanently marked, or positively controlled, until physically rendered unusable.'
      },
      supportingClauses: [AS9100D_CLAUSES['8.1.4']],
      controlDescription: {
        howItWorks: [
          'Quarantine zones are physically and system-segregated',
          'Material in quarantine cannot be issued',
          'Disposition requires MRB approval',
          'Counterfeit suspect items have regulatory hold'
        ],
        validationRules: [
          'All quarantine requires NCR linkage',
          'Disposition must be approved before material moves',
          'Scrap requires destruction confirmation'
        ],
        linkages: ['NCR', 'MRB', 'Disposition Records']
      },
      evidenceCreated: [
        'Quarantine record with reason',
        'NCR linkage',
        'Disposition request and approval',
        'Destruction confirmation (if scrapped)'
      ],
      auditorQA: [
        {
          question: 'How do you prevent nonconforming material from being used in production?',
          suggestedAnswer: 'NC material is quarantined immediately—both physically and in the system. See this quarantine status. It cannot be issued until disposition is approved. Scrap requires destruction confirmation with photo evidence...'
        }
      ],
      relatedActions: [
        { label: 'View Quarantine Items', action: 'report', target: 'quarantine-list' }
      ]
    },
    criticality: 'critical'
  },

  // ===========================================================================
  // GRN (GOODS RECEIPT NOTE)
  // ===========================================================================
  'grn.grn_number': {
    id: 'grn.grn_number',
    fieldName: 'GRN Number',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['7.5'],
      whyExists: 'Unique receipt documentation for audit trail and traceability.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['7.5'],
        quotedText: 'The organization\'s quality management system shall include documented information required by this International Standard.',
      },
      supportingClauses: [AS9100D_CLAUSES['8.4.2']],
      controlDescription: {
        howItWorks: [
          'Auto-generated sequential number',
          'Format: GRN-YYYY-NNNNN',
          'Links to PO, supplier, all received lots'
        ],
        validationRules: [
          'System-generated, cannot be modified',
          'Sequential within year'
        ],
        linkages: ['PO', 'Supplier', 'Lots Created', 'Inspection Requests']
      },
      evidenceCreated: [
        'GRN document with all receipt details',
        'Line items with quantities',
        'Attachments (packing slip, CoCs)'
      ],
      auditorQA: [
        {
          question: 'How do you document material receipts?',
          suggestedAnswer: 'Every receipt creates a GRN with unique number, linking to the PO, supplier, quantities received, and all lots created. All supporting documents are attached.'
        }
      ],
      relatedActions: []
    },
    criticality: 'high'
  },

  'grn.supplier_status': {
    id: 'grn.supplier_status',
    fieldName: 'Supplier Authorization Status',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.1.4'],
      whyExists: 'Only approved sources can supply material—counterfeit prevention.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.1.4'],
        quotedText: 'The organization shall plan, implement, and control processes for the prevention of counterfeit or suspect counterfeit part use.',
      },
      supportingClauses: [AS9100D_CLAUSES['8.4.2']],
      controlDescription: {
        howItWorks: [
          'Supplier must be on Approved Supplier List (ASL)',
          'System shows approval status and quality rating',
          'Cannot complete GRN from unapproved supplier'
        ],
        validationRules: [
          'Supplier must have APPROVED status',
          'Quality rating must meet threshold'
        ],
        linkages: ['Approved Supplier List', 'Supplier Audits', 'Quality Ratings']
      },
      evidenceCreated: [
        'Supplier authorization verification at receipt',
        'Supplier quality rating history'
      ],
      auditorQA: [
        {
          question: 'How do you verify parts come from authorized sources?',
          suggestedAnswer: 'We only purchase from approved suppliers—see this badge. The system validates supplier status at receipt. Let me show you the approved supplier list...'
        }
      ],
      relatedActions: []
    },
    criticality: 'critical'
  },

  'grn.coc_upload': {
    id: 'grn.coc_upload',
    fieldName: 'Certificate of Conformance',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.1.4'],
      whyExists: 'CoC documents material authenticity and compliance—counterfeit prevention.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.1.4'],
        quotedText: 'The organization shall plan, implement, and control processes for the prevention of counterfeit or suspect counterfeit part use.',
      },
      supportingClauses: [AS9100D_CLAUSES['8.4.2']],
      controlDescription: {
        howItWorks: [
          'CoC upload required for configured parts',
          'Cannot complete QC release without CoC',
          'CoC linked to lot for traceability',
          'Verified by QC inspector'
        ],
        validationRules: [
          'Required for parts flagged as CoC-required',
          'Must include manufacturer name and lot',
          'Must have authorized signature'
        ],
        linkages: ['Lot Record', 'QC Release', 'Supplier Documentation']
      },
      evidenceCreated: [
        'CoC document stored and linked to lot',
        'QC verification record',
        'Document retention per policy'
      ],
      auditorQA: [
        {
          question: 'How do you verify material certificates?',
          suggestedAnswer: 'CoCs are uploaded at receipt and linked to each lot. QC verifies the certificate before releasing material. Let me show you a CoC linked to a lot...'
        }
      ],
      relatedActions: []
    },
    criticality: 'critical'
  },

  'grn.inspection_routing': {
    id: 'grn.inspection_routing',
    fieldName: 'Inspection Routing',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.4.2'],
      whyExists: 'Verification activities ensure received material meets requirements.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.4.2'],
        quotedText: 'Verification activities shall include inspection or periodic testing when there is high risk of nonconformities including counterfeit parts.',
      },
      supportingClauses: [AS9100D_CLAUSES['8.6']],
      controlDescription: {
        howItWorks: [
          'Inspection requirement determined by part configuration',
          'Material routed to QC hold location',
          'Inspector assigned and due date set',
          'Release requires inspection completion'
        ],
        validationRules: [
          'Cannot skip inspection for parts requiring it',
          'Must specify inspector and location'
        ],
        linkages: ['Part Inspection Plan', 'Inspection Request', 'QC Release']
      },
      evidenceCreated: [
        'Inspection request created',
        'Assignment and due date recorded',
        'Inspection results documented'
      ],
      auditorQA: [
        {
          question: 'How do you ensure all required inspections are performed?',
          suggestedAnswer: 'Part configuration determines inspection requirements. Material is held in QC until inspection is complete. The system tracks the inspection queue and due dates.'
        }
      ],
      relatedActions: []
    },
    criticality: 'high'
  },

  'grn.fod_verification': {
    id: 'grn.fod_verification',
    fieldName: 'FOD Verification',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.1'],
      whyExists: 'Prevention, detection, and removal of foreign objects.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.1'],
        quotedText: 'The organization shall plan, implement and control the processes needed to meet the requirements.',
        aerospaceNote: 'Prevention, detection and removal of foreign objects.'
      },
      supportingClauses: [AS9100D_CLAUSES['8.5.4']],
      controlDescription: {
        howItWorks: [
          'FOD check required at receiving',
          'Checkbox confirms packaging inspected',
          'Checkbox confirms no contamination visible'
        ],
        validationRules: [
          'Both checkboxes required to complete GRN',
          'FOD issues create quarantine record'
        ],
        linkages: ['GRN Completion', 'Quality Procedures']
      },
      evidenceCreated: [
        'FOD verification checkboxes recorded',
        'Timestamp and user who verified'
      ],
      auditorQA: [
        {
          question: 'How do you prevent FOD from entering your inventory?',
          suggestedAnswer: 'Every receipt requires FOD verification—checking packaging and material for contamination. This is documented on the GRN before completion.'
        }
      ],
      relatedActions: []
    },
    criticality: 'high'
  },

  // ===========================================================================
  // ISSUE
  // ===========================================================================
  'issue.lot_selection': {
    id: 'issue.lot_selection',
    fieldName: 'Lot Selection',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.5.2'],
      whyExists: 'Lot traceability maintained through issue to production.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.5.2'],
        quotedText: 'The organization shall control the unique identification of the outputs when traceability is a requirement.',
      },
      supportingClauses: [AS9100D_CLAUSES['8.5.4']],
      controlDescription: {
        howItWorks: [
          'System suggests lots using FIFO logic',
          'User can override with documented reason',
          'Selected lot linked to work order'
        ],
        validationRules: [
          'Only RELEASED lots can be selected',
          'Cannot select expired lots',
          'Non-FIFO selection requires justification'
        ],
        linkages: ['FIFO Calculation', 'Work Order', 'Usage Trace']
      },
      evidenceCreated: [
        'Issue transaction with lot selection',
        'FIFO compliance or deviation recorded',
        'Linkage to work order/demand'
      ],
      auditorQA: [
        {
          question: 'How do you maintain lot traceability when issuing material?',
          suggestedAnswer: 'Every issue records which lot was used and links it to the work order. We can trace any product back to the specific lots of components used.'
        }
      ],
      relatedActions: []
    },
    criticality: 'critical'
  },

  'issue.fifo_recommend': {
    id: 'issue.fifo_recommend',
    fieldName: 'FIFO Recommendation',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.5.4'],
      whyExists: 'Stock rotation ensures oldest material is used first.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.5.4'],
        quotedText: 'Preservation shall include stock rotation.',
      },
      supportingClauses: [],
      controlDescription: {
        howItWorks: [
          'System calculates FIFO based on receipt date',
          'For shelf-life items, uses FEFO (First Expiry First Out)',
          'Displays FIFO rank for each available lot',
          'Green checkmark indicates FIFO-compliant selection'
        ],
        validationRules: [
          'FIFO suggestion excludes: expired, QC hold, quarantine',
          'Selecting non-FIFO lot triggers justification requirement'
        ],
        linkages: ['Lot Receipt Dates', 'Expiry Dates', 'Available Inventory']
      },
      evidenceCreated: [
        'FIFO calculation logged',
        'Recommended lot recorded',
        'Actual selection recorded'
      ],
      auditorQA: [
        {
          question: 'How do you ensure stock rotation?',
          suggestedAnswer: 'The system automatically suggests the oldest lot (FIFO) or earliest expiring lot (FEFO). If a user selects a different lot, they must document why.'
        }
      ],
      relatedActions: []
    },
    criticality: 'critical'
  },

  'issue.fifo_deviation': {
    id: 'issue.fifo_deviation',
    fieldName: 'FIFO Deviation Justification',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.5.4'],
      whyExists: 'Documents why stock rotation was not followed.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.5.4'],
        quotedText: 'Shelf life control and stock rotation.',
      },
      supportingClauses: [AS9100D_CLAUSES['7.5']],
      controlDescription: {
        howItWorks: [
          'Required when selecting non-FIFO lot',
          'Dropdown for common reasons',
          'Free text for additional details',
          'Recorded in audit trail'
        ],
        validationRules: [
          'Cannot proceed without justification',
          'Reason code required',
          'Details recommended'
        ],
        linkages: ['Issue Transaction', 'Audit Log']
      },
      evidenceCreated: [
        'FIFO deviation recorded with reason',
        'User and timestamp logged',
        'Searchable for compliance reporting'
      ],
      auditorQA: [
        {
          question: 'What happens if someone doesn\'t follow FIFO?',
          suggestedAnswer: 'The system requires a documented justification—we track these deviations. Common valid reasons include customer specification or engineering directive. Here\'s a report of FIFO deviations...'
        }
      ],
      relatedActions: [
        { label: 'View FIFO Deviations', action: 'report', target: 'fifo-deviation-report' }
      ]
    },
    criticality: 'critical'
  },

  // ===========================================================================
  // QUARANTINE
  // ===========================================================================
  'quarantine.disposition': {
    id: 'quarantine.disposition',
    fieldName: 'Disposition Decision',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.7'],
      whyExists: 'Formal disposition process for nonconforming material.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.7'],
        quotedText: 'The organization shall deal with nonconforming outputs in one or more of the following ways: correction; segregation, containment, return or suspension of provision of products and services.',
        aerospaceNote: 'Dispositions of use-as-is or repair shall only be implemented after approval by an authorized representative of the organization responsible for design.'
      },
      supportingClauses: [AS9100D_CLAUSES['10.2']],
      controlDescription: {
        howItWorks: [
          'Options: Use-As-Is, Repair, Rework, Return, Scrap',
          'Use-As-Is requires engineering approval',
          'Each disposition type has specific requirements',
          'Approval workflow based on disposition type'
        ],
        validationRules: [
          'Use-As-Is requires engineering + customer approval',
          'Scrap requires destruction confirmation',
          'Counterfeit suspect requires regulatory clearance'
        ],
        linkages: ['NCR', 'MRB', 'Engineering Approval', 'Customer Approval']
      },
      evidenceCreated: [
        'Disposition request with justification',
        'Approval records with e-signatures',
        'Execution confirmation'
      ],
      auditorQA: [
        {
          question: 'How do you disposition nonconforming material?',
          suggestedAnswer: 'We have a formal disposition workflow. Use-as-is requires engineering approval per the standard. All dispositions are documented with justification and approval records.'
        }
      ],
      relatedActions: []
    },
    criticality: 'critical'
  },

  'quarantine.scrap_confirmation': {
    id: 'quarantine.scrap_confirmation',
    fieldName: 'Scrap Destruction Confirmation',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.7'],
      whyExists: 'Scrap must be physically rendered unusable.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.7'],
        quotedText: 'Product dispositioned for scrap shall be conspicuously and permanently marked, or positively controlled, until physically rendered unusable.',
      },
      supportingClauses: [],
      controlDescription: {
        howItWorks: [
          'Physical destruction required',
          'Witness required for destruction',
          'Photo evidence uploaded',
          'System confirms destruction before closing'
        ],
        validationRules: [
          'Cannot close scrap without confirmation',
          'Must have witness name',
          'Photo evidence required'
        ],
        linkages: ['Disposition Record', 'Evidence Storage']
      },
      evidenceCreated: [
        'Destruction confirmation record',
        'Witness name and timestamp',
        'Photo evidence attached'
      ],
      auditorQA: [
        {
          question: 'How do you ensure scrapped material is actually destroyed?',
          suggestedAnswer: 'Scrap requires witnessed destruction with photo evidence. The system won\'t close the disposition without confirmation. Here\'s an example...'
        }
      ],
      relatedActions: []
    },
    criticality: 'critical'
  },

  'quarantine.counterfeit_suspect': {
    id: 'quarantine.counterfeit_suspect',
    fieldName: 'Counterfeit Suspect',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.1.4'],
      whyExists: 'Suspected counterfeit parts require regulatory hold.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.1.4'],
        quotedText: 'Counterfeit, or suspect counterfeit, parts shall be controlled to prevent reentry into the supply chain.',
      },
      supportingClauses: [AS9100D_CLAUSES['8.7']],
      controlDescription: {
        howItWorks: [
          'Regulatory hold flag prevents any disposition',
          'Requires Quality Director + Regulatory clearance',
          'Notifications sent to key personnel',
          'Special investigation workflow'
        ],
        validationRules: [
          'Cannot dispose without regulatory clearance',
          'Must complete investigation',
          'GIDEP reporting may be required'
        ],
        linkages: ['Regulatory Affairs', 'Quality Director', 'GIDEP']
      },
      evidenceCreated: [
        'Counterfeit suspect record',
        'Investigation records',
        'Regulatory clearance documentation'
      ],
      auditorQA: [
        {
          question: 'What do you do with suspected counterfeit parts?',
          suggestedAnswer: 'They go under regulatory hold immediately. The system prevents any disposition until we get clearance from Quality and Regulatory. We also notify key personnel and may need to report to GIDEP.'
        }
      ],
      relatedActions: []
    },
    criticality: 'critical'
  },

  // ===========================================================================
  // CYCLE COUNT
  // ===========================================================================
  'count.variance_reason': {
    id: 'count.variance_reason',
    fieldName: 'Variance Reason',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['10.2'],
      whyExists: 'Root cause documentation for inventory discrepancies.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['10.2'],
        quotedText: 'When a nonconformity occurs, the organization shall evaluate the need for action to eliminate the cause(s) of the nonconformity.',
      },
      supportingClauses: [AS9100D_CLAUSES['9.1']],
      controlDescription: {
        howItWorks: [
          'Reason codes for common causes',
          'Free text for additional details',
          'NCR created for significant variances',
          'Corrective action tracking'
        ],
        validationRules: [
          'Reason required for all variances',
          'NCR required for variances > threshold'
        ],
        linkages: ['NCR', 'CAPA', 'Adjustment Records']
      },
      evidenceCreated: [
        'Variance record with explanation',
        'NCR if significant',
        'Corrective action linkage'
      ],
      auditorQA: [
        {
          question: 'How do you investigate inventory discrepancies?',
          suggestedAnswer: 'All variances require documented reasons. Significant variances trigger an NCR and corrective action process. We track root causes to prevent recurrence.'
        }
      ],
      relatedActions: []
    },
    criticality: 'high'
  },

  'count.adjustment_approval': {
    id: 'count.adjustment_approval',
    fieldName: 'Adjustment Approval',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.5.1'],
      whyExists: 'Authorization required for inventory changes.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.5.1'],
        quotedText: 'Accountability for all products during production.',
      },
      supportingClauses: [AS9100D_CLAUSES['10.2']],
      controlDescription: {
        howItWorks: [
          'Approval threshold based on variance percentage',
          '< 1%: No approval needed',
          '1-5%: Supervisor approval',
          '> 5%: Manager approval with e-signature'
        ],
        validationRules: [
          'Cannot adjust without required approval',
          'E-signature for significant adjustments'
        ],
        linkages: ['Approval Workflow', 'Adjustment Transactions']
      },
      evidenceCreated: [
        'Adjustment request record',
        'Approval with timestamp and user',
        'E-signature for significant adjustments'
      ],
      auditorQA: [
        {
          question: 'Who can adjust inventory?',
          suggestedAnswer: 'It depends on the variance amount. Small adjustments are automatic, but larger ones require supervisor or manager approval with e-signature. All adjustments are logged.'
        }
      ],
      relatedActions: []
    },
    criticality: 'high'
  },

  // ===========================================================================
  // LOT MANAGEMENT (Sprint 2)
  // ===========================================================================
  'lot_part_id': {
    id: 'lot_part_id',
    fieldName: 'Part Selection',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.5.2'],
      whyExists: 'Lot must be linked to specific part for full traceability.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.5.2'],
        quotedText: 'The organization shall use suitable means to identify outputs.',
      },
      supportingClauses: [AS9100D_CLAUSES['8.4.2']],
      controlDescription: {
        howItWorks: [
          'Part ID links lot to part master',
          'Inherits shelf life, storage requirements from part',
          'Enables full backward/forward traceability'
        ],
        validationRules: [
          'Part must exist in system',
          'Part must be active status'
        ],
        linkages: ['Part Master', 'Shelf Life', 'Storage Requirements']
      },
      evidenceCreated: ['Lot-Part linkage recorded'],
      auditorQA: []
    },
    criticality: 'critical'
  },

  'lot_status': {
    id: 'lot_status',
    fieldName: 'Lot Status',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.5.1'],
      whyExists: 'Inspection status controls availability for use.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.5.1'],
        quotedText: 'Products released for subsequent use pending verification activities.',
      },
      supportingClauses: [AS9100D_CLAUSES['8.4.2'], AS9100D_CLAUSES['8.7']],
      controlDescription: {
        howItWorks: [
          'QC_HOLD: Default status, awaiting inspection (8.4.2)',
          'RELEASED: QC approved, available for use (8.6)',
          'QUARANTINE: Nonconforming material (8.7)',
          'Status transitions follow defined workflow'
        ],
        validationRules: [
          'Valid transitions enforced by system',
          'Reason required for status changes'
        ],
        linkages: ['QC Release', 'Quarantine', 'Inspection Records']
      },
      evidenceCreated: ['Status change audit trail', 'Reason documented'],
      auditorQA: [
        {
          question: 'How do you prevent use of uninspected material?',
          suggestedAnswer: 'New lots default to QC_HOLD. Only QC can release material. All status changes are logged with reason and authorizing user.'
        }
      ]
    },
    criticality: 'critical'
  },

  'lot_receipt_date': {
    id: 'lot_receipt_date',
    fieldName: 'Receipt Date',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.5.4'],
      whyExists: 'Receipt date determines FIFO order for stock rotation.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.5.4'],
        quotedText: 'Preservation shall include stock rotation.',
      },
      supportingClauses: [],
      controlDescription: {
        howItWorks: [
          'System records receipt timestamp automatically',
          'Used to calculate FIFO priority',
          'Older lots suggested first for issuance'
        ],
        validationRules: ['Auto-captured, cannot be backdated'],
        linkages: ['FIFO Calculation', 'Stock Rotation']
      },
      evidenceCreated: ['Receipt date recorded on lot'],
      auditorQA: []
    },
    criticality: 'high'
  },

  'lot_expiry_date': {
    id: 'lot_expiry_date',
    fieldName: 'Expiry Date',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.5.4'],
      whyExists: 'Shelf life control prevents use of expired material.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.5.4'],
        quotedText: 'Shelf life control.',
      },
      supportingClauses: [],
      controlDescription: {
        howItWorks: [
          'Auto-calculated from manufacture date + shelf life',
          'System alerts for lots expiring within 30 days',
          'Expired lots blocked from issuance',
          'FEFO (First Expiry First Out) prioritization'
        ],
        validationRules: [
          'Cannot issue expired material',
          'Requires disposition if expired'
        ],
        linkages: ['Expiry Alerts', 'Quarantine']
      },
      evidenceCreated: ['Expiry date tracked', 'Alert history'],
      auditorQA: [
        {
          question: 'How do you prevent use of expired material?',
          suggestedAnswer: 'System blocks issuance of expired lots. Alerts notify us 30 days before expiry. Expired material requires formal disposition.'
        }
      ]
    },
    criticality: 'critical'
  },

  'lot_ownership': {
    id: 'lot_ownership',
    fieldName: 'Ownership Type',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.5.3'],
      whyExists: 'Customer-supplied material requires special handling.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.5.3'],
        quotedText: 'The organization shall exercise care with property belonging to customers or external providers.',
      },
      supportingClauses: [],
      controlDescription: {
        howItWorks: [
          'OWNED: Company purchased material',
          'CUSTOMER: Customer-supplied material (8.5.3)',
          'CONSIGNMENT: Supplier-owned material',
          'Customer property identified and reported on'
        ],
        validationRules: [
          'Customer property must have customer ID',
          'Cannot consume without customer authorization'
        ],
        linkages: ['Customer Property Report', 'Customer Notifications']
      },
      evidenceCreated: ['Ownership type recorded', 'Customer linkage'],
      auditorQA: [
        {
          question: 'How do you identify customer-supplied material?',
          suggestedAnswer: 'Customer property is flagged at receipt. We can generate a report of all customer property on-site. Here\'s the current list...'
        }
      ]
    },
    criticality: 'high'
  },

  'lot_received_qty': {
    id: 'lot_received_qty',
    fieldName: 'Received Quantity',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.4.2'],
      whyExists: 'Accurate receipt quantity ensures inventory accountability.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.4.2'],
        quotedText: 'Verification activities for externally provided products.',
      },
      supportingClauses: [AS9100D_CLAUSES['8.5.1']],
      controlDescription: {
        howItWorks: [
          'Count verified at receipt against packing slip',
          'Discrepancies documented',
          'Receipt quantity immutable after GRN completion'
        ],
        validationRules: [
          'Must match PO line or document variance',
          'Cannot be changed after GRN completion'
        ],
        linkages: ['GRN', 'PO', 'Variance Report']
      },
      evidenceCreated: ['Receipt quantity recorded'],
      auditorQA: []
    },
    criticality: 'high'
  },

  // ===========================================================================
  // CYCLE COUNT
  // AS9100D Clause: 9.1 - Monitoring, Measurement, Analysis, Evaluation
  // AS9100D Clause: 10.2 - Nonconformity and Corrective Action
  // ===========================================================================
  'count.count_number': {
    id: 'count.count_number',
    fieldName: 'Count Number',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['9.1'],
      whyExists: 'Unique identifier for cycle count documentation and audit trail.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['9.1'],
        quotedText: 'The organization shall determine what needs to be monitored and measured.',
      },
      supportingClauses: [AS9100D_CLAUSES['7.5']],
      controlDescription: {
        howItWorks: [
          'Auto-generated sequential number (CC-YYYY-NNNNN)',
          'Tracks count type, scope, and results',
          'Links to all variance resolutions and adjustments'
        ],
        validationRules: [
          'System-generated, cannot be modified',
          'Sequential within year'
        ],
        linkages: ['Count Lines', 'Variances', 'Adjustments', 'NCRs']
      },
      evidenceCreated: [
        'Cycle count record with all details',
        'Count lines with system vs. actual quantities',
        'Variance resolution records',
        'Adjustment transactions'
      ],
      auditorQA: [
        {
          question: 'How do you verify inventory accuracy?',
          suggestedAnswer: 'We perform regular cycle counts. Each count has a unique number, freezes system quantities, records actual counts, and documents all variances with resolution actions.'
        }
      ],
      relatedActions: []
    },
    criticality: 'high'
  },

  'count.system_quantity': {
    id: 'count.system_quantity',
    fieldName: 'System Quantity',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['9.1'],
      whyExists: 'Frozen system quantity at count start provides audit baseline.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['9.1'],
        quotedText: 'The organization shall determine what needs to be monitored and measured.',
      },
      supportingClauses: [],
      controlDescription: {
        howItWorks: [
          'Quantity is frozen when count starts',
          'Immutable throughout the count process',
          'Provides baseline for variance calculation'
        ],
        validationRules: [
          'Cannot be modified after count starts',
          'Used as reference for variance calculation'
        ],
        linkages: ['Inventory Balances', 'Count Lines']
      },
      evidenceCreated: [
        'System quantity snapshot at count start',
        'Variance calculated from this baseline'
      ],
      auditorQA: [
        {
          question: 'How do you ensure the system quantity reflects the accurate baseline?',
          suggestedAnswer: 'When a count starts, system quantities are frozen. Any transactions during the count are tracked separately, ensuring a clean baseline for variance calculation.'
        }
      ],
      relatedActions: []
    },
    criticality: 'medium'
  },

  'count.variance': {
    id: 'count.variance',
    fieldName: 'Count Variance',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['10.2'],
      whyExists: 'Variances identify inventory discrepancies requiring corrective action.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['10.2'],
        quotedText: 'When a nonconformity occurs, including any arising from complaints, the organization shall react to the nonconformity.',
      },
      supportingClauses: [AS9100D_CLAUSES['9.1']],
      controlDescription: {
        howItWorks: [
          'Variance = Counted Qty - System Qty',
          'Percentage calculated for threshold checks',
          'Threshold rules determine approval requirements',
          'Large variances (>10%) trigger NCR creation'
        ],
        validationRules: [
          '0-1%: No approval needed',
          '1-5%: Supervisor approval required',
          '5-10%: Manager approval required',
          '>10%: Manager approval + NCR created'
        ],
        linkages: ['Count Lines', 'Adjustments', 'NCRs', 'CAPA']
      },
      evidenceCreated: [
        'Variance record with calculation details',
        'Resolution action and reason code',
        'Approval (if required)',
        'NCR (if significant variance)'
      ],
      auditorQA: [
        {
          question: 'How do you handle inventory discrepancies found during counts?',
          suggestedAnswer: 'All variances are documented with reason codes. Small variances can be adjusted directly, but larger ones require management approval. Significant variances (>10%) automatically trigger an NCR for root cause investigation.'
        }
      ],
      relatedActions: [
        { label: 'View Variance Report', action: 'report', target: 'variance-report' }
      ]
    },
    criticality: 'high'
  },

  'count.resolution': {
    id: 'count.resolution',
    fieldName: 'Variance Resolution',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['10.2'],
      whyExists: 'Documented resolution ensures corrective action for discrepancies.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['10.2'],
        quotedText: 'The organization shall evaluate the need for action to eliminate the cause(s) of the nonconformity.',
      },
      supportingClauses: [],
      controlDescription: {
        howItWorks: [
          'ADJUST: Create inventory adjustment to match count',
          'RECOUNT: Mark for recount if uncertainty exists',
          'NO_ACTION: Accept variance without adjustment (requires justification)'
        ],
        validationRules: [
          'All resolutions require reason code',
          'Large variances require manager approval',
          'Adjustments create immutable transaction records'
        ],
        linkages: ['Variance Records', 'Adjustment Transactions', 'Approval Workflow']
      },
      evidenceCreated: [
        'Resolution action recorded',
        'Reason code and explanation',
        'Adjustment transaction (if applicable)',
        'Approval signature (if required)'
      ],
      auditorQA: [
        {
          question: 'What options exist for resolving count variances?',
          suggestedAnswer: 'Three options: Adjust inventory to match the count, mark for recount if uncertain, or accept the variance with documented justification. Each requires a reason code, and larger variances need manager approval.'
        }
      ],
      relatedActions: []
    },
    criticality: 'high'
  },

  // =========================================================================
  // QUARANTINE MANAGEMENT - Sprint 6
  // AS9100D Clauses: 8.7, 8.1.4
  // =========================================================================

  'quarantine.status': {
    id: 'quarantine.status',
    fieldName: 'Quarantine Status',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.7'],
      whyExists: 'Tracks nonconforming material from identification through disposition.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.7'],
        quotedText: 'The organization shall ensure that outputs that do not conform to their requirements are identified and controlled to prevent their unintended use or delivery.',
        aerospaceNote: 'Counterfeit or suspect counterfeit parts shall be controlled to prevent reentry into the supply chain.'
      },
      supportingClauses: [AS9100D_CLAUSES['10.2']],
      controlDescription: {
        howItWorks: [
          'PENDING_DISPOSITION: Initial state, awaiting MRB decision',
          'AWAITING_MRB: Disposition requested, pending approval',
          'DISPOSITION_APPROVED: Ready for execution',
          'REGULATORY_HOLD: Counterfeit suspect - requires clearance',
          'CLOSED: Disposition complete'
        ],
        validationRules: [
          'Status transitions controlled by workflow',
          'Regulatory hold cannot be bypassed',
          'All status changes are audited'
        ],
        linkages: ['NCR Records', 'Disposition History', 'Audit Trail']
      },
      evidenceCreated: [
        'Status change timestamp and user',
        'Complete quarantine record',
        'Linked NCR if created',
        'Audit trail of all activities'
      ],
      auditorQA: [
        {
          question: 'How do you track nonconforming material through disposition?',
          suggestedAnswer: 'Each quarantine record has a workflow status from initial identification through final disposition. Every status change is timestamped and logged with the responsible user.'
        }
      ],
      relatedActions: []
    },
    criticality: 'critical'
  },

  'quarantine.counterfeit_suspect': {
    id: 'quarantine.counterfeit_suspect',
    fieldName: 'Counterfeit Suspect Flag',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.1.4'],
      whyExists: 'Identifies suspected counterfeit parts for special handling per AS9100D.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.1.4'],
        quotedText: 'The organization shall plan, implement, and control processes appropriate to the organization and the product for the prevention of counterfeit or suspect counterfeit part use.',
        aerospaceNote: 'Counterfeit parts shall be controlled to prevent re-entry into the supply chain.'
      },
      supportingClauses: [AS9100D_CLAUSES['8.7']],
      controlDescription: {
        howItWorks: [
          'Flags material as suspected counterfeit',
          'Automatically applies regulatory hold',
          'Creates critical severity NCR',
          'Requires regulatory clearance for disposition',
          'Cannot be released to stock without clearance'
        ],
        validationRules: [
          'Cannot disposition without regulatory approval',
          'Permanent audit record maintained',
          'Supplier notification required'
        ],
        linkages: ['NCR System', 'Regulatory Affairs', 'Supplier Quality']
      },
      evidenceCreated: [
        'Counterfeit suspect alert record',
        'NCR with critical severity',
        'Regulatory hold documentation',
        'Investigation records',
        'PERMANENT retention audit trail'
      ],
      auditorQA: [
        {
          question: 'What happens when you identify a suspected counterfeit part?',
          suggestedAnswer: 'The system immediately quarantines the material with a regulatory hold. An NCR is auto-created with CRITICAL severity. The item cannot be dispositioned until regulatory affairs grants clearance. All records are permanently retained.'
        },
        {
          question: 'How do you prevent counterfeit parts from re-entering the supply chain?',
          suggestedAnswer: 'Counterfeit suspect items are under regulatory hold in the system. They cannot be released, transferred, or used. Scrap is the typical disposition, with witnessed destruction and photo evidence.'
        }
      ],
      relatedActions: [
        { label: 'View NCR', action: 'report', target: 'ncr-view' }
      ]
    },
    criticality: 'critical'
  },

  'quarantine.disposition_type': {
    id: 'quarantine.disposition_type',
    fieldName: 'Disposition Decision',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.7'],
      whyExists: 'Documents the formal decision for handling nonconforming material.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.7'],
        quotedText: 'The organization shall deal with nonconforming outputs in one or more of the following ways: correction; segregation, containment, return or suspension; informing the customer; obtaining authorization for acceptance under concession.',
        aerospaceNote: 'Use-as-is or repair only with proper authorization. Scrap shall be conspicuously marked or positively controlled until physically rendered unusable.'
      },
      supportingClauses: [AS9100D_CLAUSES['10.2']],
      controlDescription: {
        howItWorks: [
          'USE_AS_IS: Accept with engineering concession',
          'REPAIR: Fix to specification, requires re-inspection',
          'REWORK: Rework to specification, requires re-inspection',
          'RETURN_SUPPLIER: RMA to supplier',
          'SCRAP: Destroy material (requires witness & evidence)'
        ],
        validationRules: [
          'Each type has specific approval requirements',
          'USE_AS_IS requires engineering approval',
          'SCRAP requires finance approval (cost write-off)',
          'Counterfeit requires regulatory approval',
          'All require QA approval'
        ],
        linkages: ['Engineering', 'Quality Assurance', 'Finance', 'Regulatory Affairs']
      },
      evidenceCreated: [
        'Disposition decision record',
        'Justification documentation',
        'Approval signatures (e-signature)',
        'Execution evidence (photos for scrap)'
      ],
      auditorQA: [
        {
          question: 'What are your disposition options for nonconforming material?',
          suggestedAnswer: 'Five options: Use-as-is (requires engineering concession), repair, rework, return to supplier, or scrap. Each has specific approval requirements and creates documented evidence.'
        },
        {
          question: 'Who approves use-as-is dispositions?',
          suggestedAnswer: 'Use-as-is requires both engineering and QA approval. Engineering must confirm the material meets functional requirements despite the nonconformance. Both approvals are captured with e-signatures.'
        }
      ],
      relatedActions: []
    },
    criticality: 'critical'
  },

  'quarantine.scrap_confirmation': {
    id: 'quarantine.scrap_confirmation',
    fieldName: 'Scrap Destruction Confirmation',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.7'],
      whyExists: 'Ensures scrap is physically destroyed and cannot re-enter inventory.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.7'],
        quotedText: 'Scrap shall be conspicuously marked or positively controlled until physically rendered unusable.',
        aerospaceNote: 'Counterfeit parts must be controlled to prevent re-entry into the supply chain.'
      },
      supportingClauses: [AS9100D_CLAUSES['8.1.4']],
      controlDescription: {
        howItWorks: [
          'Material held in segregated scrap area',
          'Physical destruction witnessed',
          'Photo evidence captured',
          'Destruction method documented',
          'System record finalized'
        ],
        validationRules: [
          'Requires witness name',
          'Requires photo evidence',
          'Destruction method must be specified',
          'Cannot be reversed'
        ],
        linkages: ['Scrap Area', 'Destruction Records', 'Photo Evidence']
      },
      evidenceCreated: [
        'Destruction confirmation record',
        'Witness identification',
        'Photographic evidence',
        'Destruction method',
        'Final inventory adjustment'
      ],
      auditorQA: [
        {
          question: 'How do you confirm material has been scrapped?',
          suggestedAnswer: 'Scrap requires witnessed destruction with photo evidence. The system captures the witness name, destruction method, and a photo proving the material is rendered unusable. This cannot be reversed.'
        }
      ],
      relatedActions: []
    },
    criticality: 'critical'
  },

  'quarantine.regulatory_hold': {
    id: 'quarantine.regulatory_hold',
    fieldName: 'Regulatory Hold Status',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.1.4'],
      whyExists: 'Blocks all actions on material until regulatory clearance is obtained.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.1.4'],
        quotedText: 'The organization shall plan, implement, and control processes for the prevention of counterfeit or suspect counterfeit part use.',
      },
      supportingClauses: [AS9100D_CLAUSES['8.7']],
      controlDescription: {
        howItWorks: [
          'Applied automatically for counterfeit suspects',
          'Blocks all disposition actions',
          'Requires formal regulatory clearance',
          'Clearance reference must be documented',
          'Only then can disposition proceed'
        ],
        validationRules: [
          'Cannot be bypassed or overridden',
          'Clearance must include reference number',
          'All clearances are permanently audited'
        ],
        linkages: ['Regulatory Affairs', 'NCR System', 'Audit Log']
      },
      evidenceCreated: [
        'Regulatory hold record',
        'Clearance reference (when granted)',
        'Clearance date and authorizer',
        'PERMANENT retention'
      ],
      auditorQA: [
        {
          question: 'What prevents counterfeit suspect parts from being dispositioned prematurely?',
          suggestedAnswer: 'A regulatory hold is automatically applied when material is flagged as counterfeit suspect. This hold cannot be bypassed. Regulatory affairs must formally grant clearance with a documented reference before any disposition can proceed.'
        }
      ],
      relatedActions: []
    },
    criticality: 'critical'
  },

  'quarantine.days_in_quarantine': {
    id: 'quarantine.days_in_quarantine',
    fieldName: 'Quarantine Age',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['8.7'],
      whyExists: 'Monitors aging of nonconforming material to ensure timely disposition.',
      complianceStatus: 'monitoring'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['8.7'],
        quotedText: 'The organization shall deal with nonconforming outputs.',
      },
      supportingClauses: [AS9100D_CLAUSES['9.1']],
      controlDescription: {
        howItWorks: [
          'Calculated from quarantine entry date',
          'Displayed in list views and dashboards',
          'Aging buckets: 0-7, 7-30, 30-90, 90-365, 365+ days',
          'Used for prioritization and escalation'
        ],
        validationRules: [
          'Automatic calculation',
          'No user override'
        ],
        linkages: ['Dashboard Metrics', 'Escalation Triggers']
      },
      evidenceCreated: [
        'Quarantine entry timestamp',
        'Aging visible in all views'
      ],
      auditorQA: [
        {
          question: 'How do you ensure nonconforming material is dispositioned in a timely manner?',
          suggestedAnswer: 'The system tracks days in quarantine for every item. Dashboard shows aging buckets, and items over 30 days are highlighted for management attention.'
        }
      ],
      relatedActions: []
    },
    criticality: 'medium'
  },

  'quarantine.ncr_link': {
    id: 'quarantine.ncr_link',
    fieldName: 'NCR Reference',
    quickInfo: {
      primaryClause: AS9100D_CLAUSES['10.2'],
      whyExists: 'Links quarantine to formal nonconformance record for root cause analysis.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        ...AS9100D_CLAUSES['10.2'],
        quotedText: 'When a nonconformity occurs, the organization shall react to the nonconformity, evaluate the need for action to eliminate causes, implement any action needed, review the effectiveness.',
      },
      supportingClauses: [AS9100D_CLAUSES['8.7']],
      controlDescription: {
        howItWorks: [
          'NCR created automatically for significant issues',
          'Links quarantine to corrective action process',
          'Tracks root cause investigation',
          'Enables trend analysis'
        ],
        validationRules: [
          'Counterfeit suspects always create NCR',
          'Large variances create NCR',
          'NCR cannot be deleted once linked'
        ],
        linkages: ['NCR System', 'CAPA', 'Supplier Quality']
      },
      evidenceCreated: [
        'NCR reference number',
        'Link to quarantine record',
        'Investigation documentation',
        'Corrective action records'
      ],
      auditorQA: [
        {
          question: 'How does quarantine link to your corrective action process?',
          suggestedAnswer: 'Significant quarantine events automatically create an NCR. This links to our CAPA system for root cause analysis and corrective actions. Counterfeit suspects always create a critical NCR.'
        }
      ],
      relatedActions: [
        { label: 'View NCR', action: 'report', target: 'ncr-detail' }
      ]
    },
    criticality: 'high'
  },

  // =========================================================================
  // DASHBOARD TOOLTIPS (Sprint 7)
  // =========================================================================

  'dashboard.pending_receipts': {
    id: 'dashboard.pending_receipts',
    fieldName: 'Pending Receipts',
    quickInfo: {
      primaryClause: { number: '8.4.2', title: 'External Provider Control', summary: 'Verification activities for received goods' },
      whyExists: 'Tracks incoming material awaiting receipt and inspection to ensure timely processing and prevent delays.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        number: '8.4.2',
        title: 'Type and Extent of Control',
        quotedText: 'The organization shall determine the verification activities needed to ensure that externally provided processes, products, and services meet requirements.',
        aerospaceNote: 'Inspection or periodic testing when there is high risk of nonconformities including counterfeit parts.'
      },
      supportingClauses: [
        { number: '8.1.4', title: 'Counterfeit Prevention', summary: 'Source verification on receipt' }
      ],
      controlDescription: {
        howItWorks: [
          'Pending receipts are displayed with priority indicators',
          'Urgent/overdue items highlighted in red',
          'Click-through to GRN processing workflow'
        ],
        validationRules: ['GRN status tracked from DRAFT through COMPLETED'],
        linkages: ['Purchase Orders', 'Supplier Records', 'Inspection Queue']
      },
      evidenceCreated: ['Dashboard view timestamps', 'User activity logs'],
      auditorQA: [
        {
          question: 'How do you track pending receipts to ensure timely processing?',
          suggestedAnswer: 'The dashboard shows all pending GRNs with urgency indicators. Overdue items are highlighted red and appear at the top of task lists. Management reviews the dashboard daily.'
        }
      ],
      relatedActions: [
        { label: 'View Pending GRNs', action: 'report', target: 'grn-list' }
      ]
    },
    criticality: 'high'
  },

  'dashboard.qc_hold': {
    id: 'dashboard.qc_hold',
    fieldName: 'QC Hold Items',
    quickInfo: {
      primaryClause: { number: '8.4.2', title: 'External Provider Control', summary: 'Material awaiting quality inspection' },
      whyExists: 'Monitors material held for quality verification to prevent use of uninspected goods.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        number: '8.4.2',
        title: 'Type and Extent of Control',
        quotedText: 'Verification activities shall include inspection or periodic testing when there is high risk of nonconformities.',
        aerospaceNote: 'QC hold prevents release of uninspected material to production.'
      },
      supportingClauses: [
        { number: '8.6', title: 'Release of Products', summary: 'Cannot release until verification complete' }
      ],
      controlDescription: {
        howItWorks: [
          'QC hold count displayed with aging information',
          'Oldest item age shown to prioritize inspection',
          'Click-through to inspection queue'
        ],
        validationRules: ['Material cannot be issued while in QC_HOLD status'],
        linkages: ['Inspection Records', 'Release Approvals']
      },
      evidenceCreated: ['QC hold duration tracking', 'Inspection completion records'],
      auditorQA: [
        {
          question: 'How do you ensure QC hold material is not inadvertently used?',
          suggestedAnswer: 'The system blocks issuance of QC_HOLD material. Dashboard monitoring shows aging to ensure timely inspection. The oldest hold age is displayed to management.'
        }
      ],
      relatedActions: [
        { label: 'View Inspection Queue', action: 'report', target: 'qc-queue' }
      ]
    },
    criticality: 'high'
  },

  'dashboard.expiring_soon': {
    id: 'dashboard.expiring_soon',
    fieldName: 'Expiring Soon',
    quickInfo: {
      primaryClause: { number: '8.5.4', title: 'Preservation', summary: 'Shelf life control and stock rotation' },
      whyExists: 'Monitors material approaching expiry to enable FIFO consumption and prevent waste.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        number: '8.5.4',
        title: 'Preservation',
        quotedText: 'Preservation shall include shelf life control and stock rotation.',
        aerospaceNote: 'Aerospace materials often have critical shelf life requirements affecting product safety.'
      },
      supportingClauses: [
        { number: '8.5.1', title: 'Production Control', summary: 'Material accountability includes expiry tracking' }
      ],
      controlDescription: {
        howItWorks: [
          'Lots expiring within 30/60/90 days are counted and displayed',
          'FIFO enforcement suggests oldest lots first for consumption',
          'Alerts escalate as expiry approaches'
        ],
        validationRules: ['Cannot issue expired material', 'FIFO deviation requires justification'],
        linkages: ['Stock Inquiry', 'Issue Workflow', 'Disposal Records']
      },
      evidenceCreated: ['Expiry monitoring logs', 'FIFO compliance records'],
      auditorQA: [
        {
          question: 'How do you prevent use of expired material?',
          suggestedAnswer: 'Dashboard shows lots expiring within 90 days with counts by urgency. The system enforces FIFO and blocks issuance of expired lots. Approaching expiry triggers alerts for disposition.'
        }
      ],
      relatedActions: [
        { label: 'View Expiring Lots', action: 'report', target: 'expiry-report' }
      ]
    },
    criticality: 'critical'
  },

  'dashboard.counts_due': {
    id: 'dashboard.counts_due',
    fieldName: 'Counts Due',
    quickInfo: {
      primaryClause: { number: '9.1', title: 'Monitoring and Measurement', summary: 'Inventory accuracy verification' },
      whyExists: 'Tracks scheduled cycle counts to maintain inventory accuracy and identify discrepancies.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        number: '9.1',
        title: 'Monitoring, Measurement, Analysis and Evaluation',
        quotedText: 'The organization shall determine what needs to be monitored and measured.',
        aerospaceNote: 'Inventory accuracy is a key performance indicator for aerospace operations.'
      },
      supportingClauses: [
        { number: '10.2', title: 'Corrective Action', summary: 'Count variances trigger investigation' }
      ],
      controlDescription: {
        howItWorks: [
          'Shows counts due today, this week, and overdue',
          'Overdue counts highlighted for immediate attention',
          'Click-through to count execution'
        ],
        validationRules: ['Significant variances require NCR creation'],
        linkages: ['Cycle Count Records', 'Variance Analysis', 'NCR System']
      },
      evidenceCreated: ['Count scheduling records', 'Execution timestamps', 'Accuracy metrics'],
      auditorQA: [
        {
          question: 'How do you ensure cycle counts are performed as scheduled?',
          suggestedAnswer: 'The dashboard shows all scheduled counts with due dates. Overdue counts appear in red and on user task lists. Management reviews count completion rates weekly.'
        }
      ],
      relatedActions: [
        { label: 'View Count Schedule', action: 'report', target: 'count-schedule' }
      ]
    },
    criticality: 'medium'
  },

  'dashboard.user_tasks': {
    id: 'dashboard.user_tasks',
    fieldName: 'My Tasks',
    quickInfo: {
      primaryClause: { number: '9.1', title: 'Monitoring and Measurement', summary: 'Personalized work management' },
      whyExists: 'Aggregates pending work items to ensure timely completion of inventory operations.',
      complianceStatus: 'active'
    },
    fullContext: {
      primaryClause: {
        number: '9.1',
        title: 'Monitoring, Measurement, Analysis and Evaluation',
        quotedText: 'The organization shall determine what needs to be monitored and measured, and when.',
        aerospaceNote: 'Task prioritization ensures critical operations are not delayed.'
      },
      supportingClauses: [
        { number: '8.5.1', title: 'Production Control', summary: 'Accountability for operations' }
      ],
      controlDescription: {
        howItWorks: [
          'Tasks aggregated from all inventory workflows',
          'Priority-sorted with CRITICAL/HIGH at top',
          'Overdue items highlighted in red',
          'Direct navigation to action screen'
        ],
        validationRules: ['Tasks remain until actioned', 'Overdue flagging automatic'],
        linkages: ['GRN Processing', 'Inspections', 'Cycle Counts', 'Dispositions']
      },
      evidenceCreated: ['Task completion timestamps', 'User activity logs'],
      auditorQA: [
        {
          question: 'How do you ensure critical tasks are completed timely?',
          suggestedAnswer: 'The dashboard shows personalized task lists sorted by priority. Critical and overdue items appear at the top with visual indicators. Users review their task list at start of each shift.'
        }
      ],
      relatedActions: [
        { label: 'View All Tasks', action: 'report', target: 'task-list' }
      ]
    },
    criticality: 'medium'
  }
};

/**
 * Get tooltip by ID
 * @param {string} tooltipId - Tooltip identifier
 * @returns {ComplianceTooltip|null}
 */
export const getTooltip = (tooltipId) => {
  return complianceTooltips[tooltipId] || null;
};

/**
 * Get all tooltips for a section
 * @param {string} section - Section prefix (e.g., 'stock', 'grn')
 * @returns {Object}
 */
export const getTooltipsBySection = (section) => {
  const result = {};
  Object.keys(complianceTooltips).forEach(key => {
    if (key.startsWith(`${section}.`)) {
      result[key] = complianceTooltips[key];
    }
  });
  return result;
};

/**
 * Check if clause is critical
 * @param {string} clauseNumber - Clause number
 * @returns {boolean}
 */
export const isCriticalClause = (clauseNumber) => {
  return CRITICAL_CLAUSES.includes(clauseNumber);
};

/**
 * Get criticality color
 * @param {'critical' | 'high' | 'medium' | 'low'} criticality
 * @returns {string} Tailwind color class
 */
export const getCriticalityColor = (criticality) => {
  const colors = {
    critical: 'text-blue-600',
    high: 'text-gray-500',
    medium: 'text-gray-400',
    low: 'text-gray-300'
  };
  return colors[criticality] || colors.medium;
};

export default complianceTooltips;
